import i18next from 'i18next'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import format from './format'
import resources from './resources'
import locale from './locale'
import PoeLoader from './poe/loader'

const poe = new PoeLoader(Object.keys(resources), resources)

function getLocaleName(code: string) {
	const { metadata } = (i18next.options.resources ?? {})[code].translation as Record<string, Record<string, string>>
	return metadata?.languageName
}

export function useLocalization(): {
	locale: string
	updateLocale: typeof locale.setCurrent
	locales: { code: string; name: string }[]
	t: typeof i18next.t
	f: typeof format
} {
	// This makes sure the component using this hook
	// rerenders when the locale changes
	const { t, i18n } = useTranslation()
	const locales = useMemo(
		() =>
			Object.keys(i18n.options.resources ?? {}).map(code => ({
				code,
				name: getLocaleName(code),
			})),
		[i18n.options.resources]
	)

	// Make sure format function changes with locale
	// so that we trigger memo updates
	const f = useCallback((...args: Parameters<typeof format>) => format(...args), [locale.getCurrent()])

	// Load resources from POEditor then force re-render everything
	const [, setDidLoadPoeResources] = useState(false)
	useEffect(() => poe.subscribe(() => setDidLoadPoeResources(true)), [])

	return {
		locale: locale.getCurrent(),
		updateLocale: locale.setCurrent,
		locales,
		t,
		f: f as typeof format,
	}
}
